/**
 * React Query hooks for NDT field tickets (batch 4, fieldiq-api#1157) and the
 * rate card read they pick from. Same rules as hooks/useNdtInspection.ts: every
 * key starts ['ndtInspection'], every write invalidates that prefix (a ticket
 * write moves the job's tickets, the register, and — through exam claims — the
 * job's own state), and every read spreads LOAD_FAILURE_QUERY_OPTIONS.
 *
 * A write's answer is the whole ticket as the API now holds it, so a screen
 * shows the API's amounts and subtotal next, never a figure of its own.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateNdt, unwrapNdt, unwrapNdtField, unwrapNdtList } from './useNdtInspection';
import type {
  NdtFieldTicket,
  NdtFieldTicketDetail,
  NdtLineCreate,
  NdtLineUpdate,
  NdtRate,
  NdtTicket,
  NdtTicketDetail,
  NdtTicketHeaderUpdate,
  NdtTicketSign,
} from '../types/ndt-tickets';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = v;
  return Object.keys(out).length ? out : undefined;
};

export const ndtTicketKeys = {
  tickets: (p?: Params) => ['ndtInspection', 'tickets', clean(p) ?? {}] as const,
  ticket: (id: string) => ['ndtInspection', 'ticket', id] as const,
  jobTickets: (jobId: string) => ['ndtInspection', 'jobTickets', jobId] as const,
  rates: (p?: Params) => ['ndtInspection', 'rates', clean(p) ?? {}] as const,
  fieldJobTickets: (jobId: string) => ['ndtInspection', 'fieldJobTickets', jobId] as const,
  fieldTicket: (id: string) => ['ndtInspection', 'fieldTicket', id] as const,
};

export function useNdtTickets(params?: { status?: string; jobId?: string; customerId?: string; from?: string; to?: string }) {
  return useQuery({
    queryKey: ndtTicketKeys.tickets(params),
    queryFn: async () => unwrapNdtList<NdtTicket>((await api.ndtInspection.tickets.list(clean(params))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtTicket(id: string | undefined) {
  return useQuery({
    queryKey: ndtTicketKeys.ticket(id ?? ''),
    queryFn: async () => unwrapNdt<NdtTicketDetail>((await api.ndtInspection.tickets.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtJobTickets(jobId: string | undefined) {
  return useQuery({
    queryKey: ndtTicketKeys.jobTickets(jobId ?? ''),
    queryFn: async () => unwrapNdtList<NdtTicket>((await api.ndtInspection.tickets.forJob(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The rate card in force on `on` (a calendar day). Disabled until a day is known. */
export function useNdtRates(on: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ndtTicketKeys.rates({ activeOn: on ?? undefined }),
    queryFn: async () => unwrapNdtList<NdtRate>((await api.ndtInspection.rates.list({ activeOn: on as string })).data),
    enabled: enabled && !!on,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useTicketWrite<V>(fn: (v: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: V) => {
      const res = await fn(v);
      return unwrapNdt<NdtTicketDetail>(res.data);
    },
    onSuccess: () => invalidateNdt(qc),
  });
}

export const useCloseNdtDay = () =>
  useTicketWrite(({ jobId, workDate }: { jobId: string; workDate: string }) => api.ndtInspection.tickets.closeDay(jobId, workDate));
export const useUpdateNdtTicket = () =>
  useTicketWrite(({ id, body }: { id: string; body: NdtTicketHeaderUpdate }) => api.ndtInspection.tickets.update(id, body));
export const useAddNdtTicketLine = () =>
  useTicketWrite(({ ticketId, body }: { ticketId: string; body: NdtLineCreate }) => api.ndtInspection.tickets.addLine(ticketId, body));
export const useUpdateNdtTicketLine = () =>
  useTicketWrite(({ lineId, body }: { lineId: string; body: NdtLineUpdate }) => api.ndtInspection.tickets.updateLine(lineId, body));
export const useRemoveNdtTicketLine = () =>
  useTicketWrite(({ lineId, reason }: { lineId: string; reason: string | null }) => api.ndtInspection.tickets.removeLine(lineId, reason));
export const useRepriceNdtTicket = () => useTicketWrite((id: string) => api.ndtInspection.tickets.reprice(id));
export const useApproveNdtTicket = () => useTicketWrite((id: string) => api.ndtInspection.tickets.approve(id));
export const useVoidNdtTicket = () =>
  useTicketWrite(({ id, reason }: { id: string; reason: string }) => api.ndtInspection.tickets.void(id, reason));

/** Re-run the completion sweep on a complete job. Answers the tickets it created or updated. */
export function useRebuildNdtTickets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => unwrapNdtList<NdtTicket>((await api.ndtInspection.tickets.rebuild(jobId)).data),
    onSuccess: () => invalidateNdt(qc),
  });
}

// ─── The field surface (snake_case) ──────────────────────────────────────────

export function useNdtFieldJobTickets(jobId: string | undefined) {
  return useQuery({
    queryKey: ndtTicketKeys.fieldJobTickets(jobId ?? ''),
    queryFn: async () => {
      const { data, meta } = unwrapNdtField<NdtFieldTicket[]>((await api.ndtInspection.field.jobTickets(jobId as string)).data);
      return { data: Array.isArray(data) ? data : [], meta };
    },
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useNdtFieldTicket(id: string | undefined) {
  return useQuery({
    queryKey: ndtTicketKeys.fieldTicket(id ?? ''),
    queryFn: async () => unwrapNdtField<NdtFieldTicketDetail>((await api.ndtInspection.field.ticket(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function useFieldTicketWrite<V>(fn: (v: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: V) => unwrapNdtField<NdtFieldTicketDetail>((await fn(v)).data),
    onSuccess: () => invalidateNdt(qc),
  });
}

export const useCloseNdtFieldDay = () =>
  useFieldTicketWrite(({ jobId, workDate }: { jobId: string; workDate: string }) => api.ndtInspection.field.closeDay(jobId, workDate));
export const useAddNdtFieldTicketLine = () =>
  useFieldTicketWrite(({ ticketId, body }: { ticketId: string; body: NdtLineCreate }) => api.ndtInspection.field.addLine(ticketId, body));
export const useSignNdtFieldTicket = () =>
  useFieldTicketWrite(({ ticketId, body }: { ticketId: string; body: NdtTicketSign }) => api.ndtInspection.field.sign(ticketId, body));
